import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { DotsHorizontal, ImageSquare } from "@oai/platform/ui/Icon";
import { Image } from "@oai/platform/ui/Image";

import SafeExternalLink, { getSafeExternalHref } from "@/components/common/SafeExternalLink";
import useSofaClient, { useSofaAuth } from "@/hooks/useSofaClient";
import { sofaGetQueryOptions } from "@/lib/sofaQueryOptions";

import { messages } from "../localization/CreativePreviewContent.messages";
import { getCroppedImageStyle } from "./AdImageCrop";

import type { AdImageCrop } from "./AdImageCrop";

type AdReviewCreativePreviewProps = {
  advertiserName?: string | null;
  body: string;
  imageUrl?: string | null;
  imageCrop?: AdImageCrop | null;
  previewImageUrl?: string | null;
  targetUrl?: string | null;
  title: string;
};

export function AdAccountReviewCreativePreview({
  body,
  imageUrl,
  imageCrop,
  previewImageUrl,
  targetUrl,
  title,
}: Omit<AdReviewCreativePreviewProps, "advertiserName">) {
  const client = useSofaClient();
  const auth = useSofaAuth();
  const { data: adAccount, isLoading } = useQuery(
    sofaGetQueryOptions(client, { path: "/v1/ui/ad_account" }, auth),
  );
  return (
    <AdReviewCreativePreview
      advertiserName={adAccount?.name}
      body={body}
      imageUrl={imageUrl}
      imageCrop={imageCrop}
      isAdvertiserLoading={isLoading}
      previewImageUrl={previewImageUrl === undefined ? adAccount?.preview_url : previewImageUrl}
      targetUrl={targetUrl}
      title={title}
    />
  );
}

export function AdReviewCreativePreview({
  advertiserName,
  body,
  imageUrl,
  imageCrop,
  isAdvertiserLoading = false,
  previewImageUrl,
  targetUrl,
  title,
}: AdReviewCreativePreviewProps & { isAdvertiserLoading?: boolean }) {
  const safeTargetUrl = getSafeExternalHref(targetUrl);

  return (
    <div
      className="border-default bg-surface flex h-[118px] w-full min-w-0 overflow-hidden rounded-lg border"
      data-testid="ad-review-creative-preview"
    >
      {imageUrl ?
        <div className="relative aspect-square h-full shrink-0 overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className={
              imageCrop == null ?
                "absolute inset-0 h-full w-full object-cover object-center"
              : "absolute object-fill"
            }
            style={imageCrop == null ? undefined : getCroppedImageStyle(imageCrop)}
          />
        </div>
      : <div className="bg-surface-tertiary text-tertiary flex aspect-square h-full shrink-0 items-center justify-center">
          <ImageSquare aria-hidden="true" className="size-5" />
        </div>
      }
      {safeTargetUrl ?
        <SafeExternalLink
          href={safeTargetUrl}
          className="flex min-w-0 flex-1 flex-col p-4 no-underline"
          title={safeTargetUrl}
        >
          <AdReviewCreativePreviewContent
            advertiserName={advertiserName}
            body={body}
            isAdvertiserLoading={isAdvertiserLoading}
            previewImageUrl={previewImageUrl}
            title={title}
          />
        </SafeExternalLink>
      : <div className="flex min-w-0 flex-1 flex-col p-4">
          <AdReviewCreativePreviewContent
            advertiserName={advertiserName}
            body={body}
            isAdvertiserLoading={isAdvertiserLoading}
            previewImageUrl={previewImageUrl}
            title={title}
          />
        </div>
      }
    </div>
  );
}

function AdReviewCreativePreviewContent({
  advertiserName,
  body,
  isAdvertiserLoading,
  previewImageUrl,
  title,
}: {
  advertiserName?: string | null;
  body: string;
  isAdvertiserLoading: boolean;
  previewImageUrl?: string | null;
  title: string;
}) {
  const intl = useIntl();
  const resolvedAdvertiserName =
    advertiserName?.trim() || intl.formatMessage(messages.advertiserFallbackName);

  return (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="relative size-4 shrink-0 overflow-hidden rounded-full bg-white p-0.5">
          <Image
            key={previewImageUrl}
            src={previewImageUrl ?? undefined}
            alt={intl.formatMessage(messages.profileAlt)}
            className="size-full object-contain"
          />
        </div>
        {isAdvertiserLoading && advertiserName == null ?
          <div className="bg-surface-tertiary h-4 min-w-0 flex-1 animate-pulse rounded" />
        : <span className="text-primary min-w-0 flex-1 truncate text-xs font-medium">
            {resolvedAdvertiserName}
          </span>
        }
        <span className="bg-surface-tertiary text-secondary h-5 shrink-0 rounded-full px-2 text-xs leading-5 font-medium">
          {intl.formatMessage(messages.adLabel)}
        </span>
        <DotsHorizontal className="text-tertiary size-4 shrink-0" aria-hidden="true" />
      </div>
      <div className="mt-2 min-w-0">
        <div className="text-primary truncate text-sm font-medium">{title}</div>
        <div className="text-secondary line-clamp-2 text-sm">{body}</div>
      </div>
    </>
  );
}
